import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mantine/dates";
import { Popover } from "@mantine/core";
import SearchIcon from "@mui/icons-material/Search";
import PlaceIcon from "@mui/icons-material/Place";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import Nav from "../NavBar/Nav";
import FooterNew from "../NavBar/FooterNew";
import base_url from "../../api/bootapi";
import { formatDate, formatDateRange } from "./experienceUtils";
import heroImage from "../../assets/tour-banner2.jpg";
import catParis from "../../assets/split-1.jpg";
import catDubai from "../../assets/split-2.jpg";
import catBali from "../../assets/split-3.jpg";
import catRome from "../../assets/split-4.jpg";
import viator1 from "./viator1.png";
import viator2 from "./viator2.png";
import viator3 from "./viator3.png";
import {
  IconPhoneCall,
  IconGift,
  IconStar,
  IconCalendarCheck
} from "@tabler/icons-react";
import "./Experiences.css";
import { CalendarMonth, Close, CropSquareSharp, PlaceOutlined } from "@mui/icons-material";

const POPULAR_DESTINATIONS = ["Paris", "Dubai", "New York", "Rome", "Bali", "Bangkok"];

const CATEGORY_TILES = [
  { name: "Paris", label: "Paris, France", image: catParis },
  { name: "Dubai", label: "Dubai, UAE", image: catDubai },
  { name: "Bali", label: "Bali, Indonesia", image: catBali },
  { name: "Rome", label: "Rome, Italy", image: catRome },
];

const images = [
  viator1,
  viator2,
  viator3,
];

export default function ExperienceSearch() {
  const navigate = useNavigate();

  const [destinationQuery, setDestinationQuery] = useState("");
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDestDropdown, setShowDestDropdown] = useState(false);
  const [destLoading, setDestLoading] = useState(false);
  const [autoSearching, setAutoSearching] = useState(false);

  const [showMobileDestination, setShowMobileDestination] = useState(false);
  const [showMobileDates, setShowMobileDates] = useState(false);
  const [showMobileForm, setShowMobileForm] = useState(false);

  const [dateRange, setDateRange] = useState([null, null]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");


   const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const destWrapperRef = useRef(null);
  // Tracks the most recent query we asked for, so an older, slower
  // response can't clobber the results of a newer one.
  const latestQueryRef = useRef("");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (destWrapperRef.current && !destWrapperRef.current.contains(e.target)) {
        setShowDestDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // IMPORTANT: debounce() must be created ONCE and reused, not rebuilt on
  // every render. Previously this was declared as a plain `const` in the
  // component body, so every keystroke (which triggers a re-render via
  // setDestinationQuery) produced a brand-new debounced function. Each of
  // those independent instances still fired its own timer 400ms later,
  // so instead of debouncing to one request, every keystroke fired its
  // own API call. useRef keeps a single debounced instance alive across
  // renders so the 400ms debounce actually works.
  const fetchDestinationsRef = useRef(
    debounce(async (query, onDone) => {
      if (!query || query.trim().length < 2) {
        setDestinationOptions([]);
        setProductOptions([]);
        return;
      }
      latestQueryRef.current = query;
      setDestLoading(true);
      try {
        const response = await axios.get(
          `${base_url.api1}/tourapp/viator/destinations?search=${encodeURIComponent(
            query
          )}&page=1&count=10`
        );
        // Guard against out-of-order responses: if the user has since
        // typed something else, ignore this now-stale result.
        if (latestQueryRef.current !== query) return;
       const data = response.data;
      const destResults = data && data.status ? data.destinations || [] : [];
        const prodResults = data && data.status ? data.products || [] : [];
        
        const finalDestinations = destResults?.filter((dest) => dest.destination_id)
        console.log(finalDestinations)

      setDestinationOptions(finalDestinations);
        setProductOptions(prodResults); // Save products to state
        
        if (onDone) onDone({ destinations: destResults, products: prodResults });
      } catch (error) {
        if (latestQueryRef.current !== query) return;
        console.error("Error fetching destinations:", error);
       setProductOptions([]);
      if (onDone) onDone({ destinations: [], products: [] });
      } finally {
        if (latestQueryRef.current === query) setDestLoading(false);
      }
    }, 400)
  );
  const fetchDestinations = fetchDestinationsRef.current;

  const handleDestinationInputChange = (e) => {
    const value = e.target.value;
    setDestinationQuery(value);
    setSelectedDestination(null);
    setShowDestDropdown(true);
    fetchDestinations(value);
  };

  const searchRef = useRef(null);

  const handleSelectDestination = (destination) => {
    console.log(destination)
    setSelectedDestination(destination);
    setSelectedProduct(null);
    setDestinationQuery(destination.name);
    setShowDestDropdown(false);

    setShowMobileDestination(false);

  };

  const goToResults = (destination) => {
    const params = new URLSearchParams({
      destination_id: String(destination.destination_id),
      name: destination.name,
      currency:  "USD",
    });
    if (dateRange[0] && dateRange[1]) {
      params.set("start", formatDate(dateRange[0]));
      params.set("end", formatDate(dateRange[1]));
    }
    navigate(`/experiences/results?${params.toString()}`, {
      state: { destination, dateRange },
    });
  };

  

  const handleSearch = () => {
    if (!selectedDestination && !selectedProduct) {
      setErrorMsg("Please choose a place or activity from the list.");
      return;
    }
    setErrorMsg("");

    if (selectedDestination) {
      
      goToResults(selectedDestination);
    } else {
       navigate(`/experiences/details/${selectedProduct.productCode}`, {
      state: { selectedProduct, destination:null },
    });
    }
  };



  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // Popular chip / category tile: fill in the field, look the place up, and
  // jump straight to results with the best match — no dead click needed.
  const handleQuickPick = (name) => {
    setErrorMsg("");
    setDestinationQuery(name);
    setAutoSearching(true);
    fetchDestinations(name, (results) => {
      setAutoSearching(false);
      if (results && results.length) {
        const bestMatch =
          results.find((d) => d.name.toLowerCase() === name.toLowerCase()) || results[0];
        setSelectedDestination(bestMatch);
        setDestinationQuery(bestMatch.name);
        goToResults(bestMatch);
      } else {
        setErrorMsg(`We couldn't find experiences for "${name}" right now.`);
      }
    });
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSelectedDestination(null)
    setDestinationQuery(product?.title);
    setShowDestDropdown(false);
    setShowMobileDestination(false);

  }

  const dateDisplay =
    dateRange[0] && dateRange[1] ? formatDateRange(dateRange[0], dateRange[1]) : "";

  return (
    <div className="exp-scope">

       {
        showMobileDestination &&  <div className="exp-form-mobile" style={{zIndex:999,height:'100%'}}>
        <div style={{position:'relative',height:'100%'}}>
          <div className="close-mobile" onClick={()=>setShowMobileDestination(false)}>
            <Close fontSize="small"/>

        </div>
        <h6>Place or activity</h6>

          <div className="place-mobile" style={{marginTop:'30px',padding:"0px",paddingLeft:'20px'}} >
            
            <SearchIcon fontSize="small" color="#222" />
            
             <input
                type="text"
                placeholder="Search for a place or activity"
                value={destinationQuery}
                onChange={handleDestinationInputChange}
               
                onKeyDown={handleKeyDown}
                autoComplete="off"
            />
            
           
            

          </div>
          


          {destinationQuery.trim().length >= 2 && (
                <div className="exp-dest-dropdown-mobile" style={{marginTop:'20px',height:'80%',overflow:'auto'}}>
                  {destLoading && (
                    <div className="exp-dest-option exp-dest-loading">Searching...</div>
                  )}
                  {!destLoading && destinationOptions.length === 0 && (
                    <div className="exp-dest-option exp-dest-empty">
                      No matching places found
                    </div>
                  )}
                  {!destLoading &&
                    destinationOptions.map((dest, index) => {
                      // The row that matches what was typed reads as a real
                      // place (pin icon, highlighted) — every other partial
                      // match reads as a related suggestion (ticket icon),
                      // mirroring the reference design's mix of icon types.
                      const isExactMatch =
                        dest.name.trim().toLowerCase() ===
                        destinationQuery.trim().toLowerCase();
                      return (
                        <div
                          key={dest.destination_id || `${dest.name}-${index}`}
                          className={`exp-dest-option${
                            isExactMatch ? " exp-dest-option-highlight" : ""
                          }`}
                          onClick={() => handleSelectDestination(dest)}
                        >
                          {isExactMatch ? (
                            <PlaceOutlined fontSize="large" />
                          ) : (
                            <ConfirmationNumberOutlinedIcon fontSize="large" />
                          )}
                          <div>
                            <div className="exp-dest-name">{dest.name}</div>
                            {/* <div className="exp-dest-type">
                              {dest.parent_destination_name || dest.type}
                            </div> */}
                          </div>
                        </div>
                      );
                    })}
                
                {!destLoading && productOptions.length > 0 && (
      <div className="exp-dest-section-title" style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 'bold', color: '#666', backgroundColor: '#f9f9f9', borderTop: '1px solid #eee' }}>
        Tours & Activities
      </div>
    )}
    {!destLoading &&
      productOptions.map((product, index) => {
        // Extract the smallest image variant for the dropdown thumbnail
        const coverImage = product.images?.[0]?.variants?.find(v => v.width === 75)?.url 
          || product.images?.[0]?.variants?.[0]?.url;

        return (
          <div
            key={product.productCode || `prod-${index}`}
            className="exp-dest-option"
            onClick={() => handleSelectProduct(product)} // Make sure to define this handler!
          >
            {coverImage ? (
              <img 
                src={coverImage} 
                alt={product.title} 
                style={{ width: 35, height: 35, borderRadius: 4, objectFit: 'cover', marginRight: 10 }} 
              />
            ) : (
              <ConfirmationNumberOutlinedIcon fontSize="large" style={{ marginRight: 10, color: "gray" }} />
            )}
            <div style={{ overflow: 'hidden' }}>
              <div className="exp-dest-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {product.title}
              </div>
              <div className="exp-dest-type">
                From ${product.pricing?.summary?.fromPrice} {product.pricing?.currency} • {product.reviews?.combinedAverageRating?.toFixed(1)} ★
              </div>
            </div>
          </div>
        );
      })}
                 
                </div>
              )}

         
      </div>
      </div>
      }


        {
        showMobileDates &&  <div className="exp-form-mobile" style={{zIndex:999,height:'100%'}}>
        <div style={{position:'relative',height:'100%'}}>
          <div className="close-mobile" onClick={()=>setShowMobileDates(false)}>
            <Close fontSize="small"/>

        </div>
        <h6>Select Dates</h6>

           <div className="date-dropdown" style={{marginTop:'30px'}}>
                    <DatePicker
                  type="range"
                  value={dateRange}
                  onChange={setDateRange}
                  minDate={new Date()}
                    allowSingleDateInRange
                    size='md'
                     
                    
                  />
                  
                  <div className="exp-btn-wrapper">
                    <button className="reset-btn" onClick={()=>setDateRange([null,null])}>Reset</button>
                    <button className="apply-btn" onClick={()=>setShowMobileDates(false)}>Apply</button>
                  </div>
              </div>
          


        

         
      </div>
      </div>
      }
      {
        showMobileForm && <div className="exp-form-mobile" style={{background:'rgba(0,0,0,0.7)',padding:'0px',height:'100%'}} onClick={()=>setShowMobileForm(false)}>
        <div style={{position:'relative',background:"#fff",padding:'20px'}} onClick={(e)=>e.stopPropagation()}>
          <div className="close-mobile" style={{top:'20px',right:'20px'}} onClick={()=>setShowMobileForm(false)}>
            <Close fontSize="small"/>

        </div>
        <h6>Change your search</h6>

          <div className="place-mobile" style={{marginTop:'30px'}} onClick={()=>setShowMobileDestination(true)}>
            
             <SearchIcon fontSize="small" color="#222"/>
            {
              destinationQuery || <span style={{color:'#ccc'}}>Search for a place or activity</span>
            }
            

        </div>

          <div className="place-mobile" style={{marginTop:'15px'}} onClick={()=>setShowMobileDates(true)}>
            
             <CalendarMonth fontSize="small" color="#222"/>
             {
              dateDisplay || <span style={{color:'#ccc'}}>Search Dates</span>
            }
            
          
          </div>

          {errorMsg && <p style={{color:'red',fontSize:'12px',marginTop:'10px'}}>{errorMsg}</p>}
          
          <button className="apply-btn" style={{marginTop:'20px'}} onClick={handleSearch}>
            Search
          </button>
          </div>
          
      </div>
}
      

      
     

     




      <div style={{ position: "relative" }}>
        <Nav />
      </div>

      <div style={{position:'relative'}}> 

      
       <div className="hero-slider">

      {images.map((image, index) => (
        <img
          key={image}
          src={image}
          alt=""
          className={`hero-slide ${
            currentSlide === index ? "active" : ""
          }`}
        />
      ))}

      
        


        

        </div>
        
        <div className="slider-dots">
        {images.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`slider-dot ${
              currentSlide === index ? "active" : ""
            }`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
        </div>
      
      <div className='search-wrapper-new'>

          <h1 className="search-h1">Do more with Viator</h1>
          <p className="search-p">
            Plan better with 400,000+ travel experiences.
          </p>
           <div className="exp-search-card"  ref={destWrapperRef}>

             {showDestDropdown  && (
                <div className="exp-dest-dropdown">
                  {destLoading && (
                    <div className="exp-dest-option exp-dest-loading">Searching...</div>
                  )}
                  {!destLoading && destinationOptions.length === 0 && (
                    <div className="exp-dest-option exp-dest-empty">
                      No matching places found
                    </div>
                  )}
                  {!destLoading &&
                    destinationOptions.map((dest, index) => {
                      // The row that matches what was typed reads as a real
                      // place (pin icon, highlighted) — every other partial
                      // match reads as a related suggestion (ticket icon),
                      // mirroring the reference design's mix of icon types.
                      const isExactMatch =
                        dest?.name?.trim().toLowerCase() ===
                        destinationQuery.trim().toLowerCase();
                      return (
                        <div
                          key={dest.destination_id || `${dest.name}-${index}`}
                          className={`exp-dest-option${
                            isExactMatch ? " exp-dest-option-highlight" : ""
                          }`}
                          onClick={() => handleSelectDestination(dest)}
                        >
                          {isExactMatch ? (
                            <PlaceOutlined fontSize="large" />
                          ) : (
                            <ConfirmationNumberOutlinedIcon fontSize="large" />
                          )}
                          <div>
                            <div className="exp-dest-name">{dest.name}</div>
                            {/* <div className="exp-dest-type">
                              {dest.parent_destination_name || dest.type}
                            </div> */}
                          </div>
                        </div>
                      );
                    })}
                
                {/* --- RENDER PRODUCTS --- */}
    {!destLoading && productOptions.length > 0 && (
      <div className="exp-dest-section-title" style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 'bold', color: '#666', backgroundColor: '#f9f9f9', borderTop: '1px solid #eee' }}>
        Tours & Activities
      </div>
    )}
    {!destLoading &&
      productOptions.map((product, index) => {
        // Extract the smallest image variant for the dropdown thumbnail
        const coverImage = product.images?.[0]?.variants?.find(v => v.width === 75)?.url 
          || product.images?.[0]?.variants?.[0]?.url;

        return (
          <div
            key={product.productCode || `prod-${index}`}
            className="exp-dest-option"
            onClick={() => handleSelectProduct(product)} // Make sure to define this handler!
          >
            {coverImage ? (
              <img 
                src={coverImage} 
                alt={product.title} 
                style={{ width: 35, height: 35, borderRadius: 4, objectFit: 'cover', marginRight: 10 }} 
              />
            ) : (
              <ConfirmationNumberOutlinedIcon fontSize="large" style={{ marginRight: 10, color: "gray" }} />
            )}
            <div style={{ overflow: 'hidden' }}>
              <div className="exp-dest-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {product.title}
              </div>
              <div className="exp-dest-type">
                From ${product.pricing?.summary?.fromPrice} {product.pricing?.currency} • {product.reviews?.combinedAverageRating?.toFixed(1)} ★
              </div>
            </div>
          </div>
        );
      })}
                  {!destLoading && (
                    <div
                      className="exp-dest-option exp-dest-option-search"
                      onClick={() => handleQuickPick(destinationQuery)}
                    >
                      <SearchIcon fontSize="small" />
                      <div className="exp-dest-name">
                        Search for {destinationQuery}
                      </div>
                    </div>
                  )}
                </div>
              )}
            <div className="exp-search-field" onClick={() => {
    searchRef?.current?.focus();
  }}>
              <label>Where to?</label>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search for a place or activity"
                value={destinationQuery}
                onChange={handleDestinationInputChange}
                onFocus={() => setShowDestDropdown(true)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
              />
             
            </div>

            <div className="divider"></div>

            <Popover
              opened={showDatePicker}
              onChange={setShowDatePicker}
              position="bottom-start"
              shadow="md"
              withinPortal
               middlewares={{
    flip: false,
  }}
            >
              <Popover.Target>
                <div
                  className="exp-search-field exp-search-when"
                  onClick={() => setShowDatePicker((o) => !o)}
                >
                  <div>
<label>When</label>
                  <input type="text" readOnly value={dateDisplay} placeholder="Select Dates" />
                  </div>

                    <button
              className="exp-search-btn"
              onClick={handleSearch}
              disabled={autoSearching}
              aria-label="Search experiences"
            >
              <SearchIcon />
            </button>
                  
                </div>
              </Popover.Target>
              <Popover.Dropdown>
                <div className="date-dropdown">
                    <DatePicker
                  type="range"
                  value={dateRange}
                  onChange={setDateRange}
                  minDate={new Date()}
                    allowSingleDateInRange
                    size='md'
                     
                    
                  />
                  
                  <div className="exp-btn-wrapper">
                    <button className="reset-btn" onClick={()=>setDateRange([null,null])}>Reset</button>
                    <button className="apply-btn" onClick={()=>setShowDatePicker(false)}>Apply</button>
                  </div>
              </div>
              </Popover.Dropdown>
            </Popover>

            

          
          </div>

          <div className="exp-search-card-mobile" onClick={()=>setShowMobileForm(true)}>
            <SearchIcon fontSize="small" />
            
            Search for a place or activity


          </div>


          {errorMsg && <div className="exp-error-msg">{errorMsg}</div>}

        </div>

      </div>
      

      <section className="why-viator">
  <h2>Why book with Viator?</h2>

  <div className="why-viator-grid">

    <div className="why-item">
      <div className="why-icon icon-pink">
        <IconPhoneCall size={28} stroke={1.8} />
      </div>

      <h3>24/7 customer support</h3>

      <p>
        No matter the time zone, we’re here to help.
      </p>
    </div>

    <div className="why-item">
      <div className="why-icon icon-green">
        <IconGift size={28} stroke={1.8} />
      </div>

      <h3>Earn rewards</h3>

      <p>
        Explore, earn, redeem, and repeat with our loyalty program.
      </p>
    </div>

    <div className="why-item">
      <div className="why-icon icon-yellow">
        <IconStar size={28} stroke={1.8} />
      </div>

      <h3>Millions of reviews</h3>

      <p>
        Plan and book with confidence using reviews from fellow travelers.
      </p>
    </div>

    <div className="why-item">
      <div className="why-icon icon-blue">
        <IconCalendarCheck size={28} stroke={1.8} />
      </div>

      <h3>Plan your way</h3>

      <p>
        Stay flexible with free cancellation and the option to reserve now
        and pay later at no additional cost.
      </p>
    </div>

  </div>
</section>



    

    

      <div className="exp-container">
        <div className="exp-section-heading">
          <h2>Popular right now</h2>
          <p>Hand-picked destinations where travelers are booking the most experiences this season.</p>
        </div>
        <div className="exp-category-grid">
          {CATEGORY_TILES.map((tile) => (
            <div
              key={tile.name}
              className="exp-category-card"
              onClick={() => handleQuickPick(tile.name)}
            >
              <img src={tile.image} alt={tile.label} />
              <span>{tile.label}</span>
            </div>
          ))}
        </div>
      </div>

      <FooterNew />
    </div>
  );
}
